/**
 * Image card for the media grid.
 */
import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Badge } from '@/components/ui';
import type { MediaImage } from '@/types/media';
import { formatFileSize, getDisplayUrl } from '@/types/media';
import { debug } from '@/utils/debug'; // [MEDIA TIMING] - remove after perf work

interface ImageCardProps {
  image: MediaImage;
  isSelected: boolean;
  onSelect: (id: string, multiSelect: boolean) => void;
  onClick: (id: string) => void;
}

export function ImageCard({ image, isSelected, onSelect, onClick }: ImageCardProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      e.preventDefault();
      onSelect(image.id, true);
    } else {
      onClick(image.id);
    }
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(image.id, true);
  };

  return (
    <div
      className={`
        group relative bg-white rounded-lg border overflow-hidden cursor-pointer
        transition-all duration-150 hover:shadow-md
        ${isSelected ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-200 hover:border-gray-300'}
      `}
      onClick={handleClick}
    >
      {/* Selection checkbox - always visible on mobile, hover on desktop */}
      <div
        className={`
          absolute top-2 left-2 z-10 w-5 h-5 rounded border-2 flex items-center justify-center
          transition-all cursor-pointer
          ${isSelected
            ? 'bg-blue-500 border-blue-500'
            : 'bg-white/80 border-gray-300 md:opacity-0 md:group-hover:opacity-100'
          }
        `}
        onClick={handleCheckboxClick}
      >
        {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
      </div>

      {/* Image - use thumbnail for faster loading */}
      <div className="aspect-square bg-gray-100 overflow-hidden">
        <img
          src={getDisplayUrl(image)}
          alt={image.alt_text || image.original_filename}
          className="w-full h-full object-contain transition-transform group-hover:scale-105"
          loading="lazy"
          onLoad={() => {
            // [MEDIA TIMING] - remove after perf work
            if (!debug.isDev()) return;
            const url = getDisplayUrl(image);
            const entries = performance.getEntriesByName(url) as PerformanceResourceTiming[];
            const e = entries[entries.length - 1];
            if (e) {
              debug.log('[MEDIA TIMING] thumb load', {
                id: image.id,
                duration_ms: Math.round(e.duration),
                ttfb_ms: Math.round(e.responseStart - e.requestStart),
                transfer_bytes: e.transferSize,
              });
            }
          }}
          onError={() => {
            console.error('Image failed to load:', getDisplayUrl(image));
            console.error('Image details:', { id: image.id, filename: image.filename });
          }}
        />
      </div>

      {/* Category badge — only shown for non-Approved (Approved is the default state) */}
      {image.category !== 'approved' && (
        <div className="absolute top-2 right-2 z-10">
          <Badge variant={image.category === 'targeted' ? 'warning' : 'secondary'}>
            {image.category === 'targeted' ? 'Targeted' : 'Reference'}
          </Badge>
        </div>
      )}

      {/* Info overlay. For Targeted images the filename now carries the
          platform/placement context (composed server-side as
          "{source basename} — {placement display}", no .jpg suffix — the
          display name is a UX label, not a download target), so the previous
          secondary "google_ads · google_ads.square_marketing_image" line is
          dropped to avoid duplication. */}
      <div className="p-2">
        <p className="text-sm font-medium text-gray-900 truncate" title={image.original_filename}>
          {image.original_filename}
        </p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-xs text-gray-500">{formatFileSize(image.file_size_bytes)}</span>
          <span className="text-xs text-gray-400">
            {image.width}x{image.height}
          </span>
        </div>

        {/* Tags */}
        {image.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {image.tags.slice(0, 3).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
              >
                {tag.name}
              </span>
            ))}
            {image.tags.length > 3 && (
              <span className="text-xs text-gray-400">+{image.tags.length - 3}</span>
            )}
          </div>
        )}

        {/* Usage indicator */}
        {image.usage_count > 0 && (
          <div className="mt-2 text-xs text-green-600">
            Used in {image.usage_count} {image.usage_count === 1 ? 'place' : 'places'}
          </div>
        )}
      </div>
    </div>
  );
}
